/* eslint-disable max-len */
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDisclosure, useSteps, useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { BookableServiceV2, Contact } from '@acp/shared';
import { submitBookingFormSchema, SubmitBookingFormSchemaValues } from '../components/booking-modal/form-scheme';
import { useBookableServices } from '../../booking/hooks/use-booking';
import { useConfirmBookingMutation } from '../components/booking-modal/use-confirm-booking-mutation';
import { BookingStep, bookingSteps } from '../components/booking-modal/booking-steps';

function useBookingForm({
    propertyId,
    isOpen,
    overrideBookableServices,
    onClose,
    onBookingCreated,
}: {
    propertyId: number;
    isOpen?: boolean;
    overrideBookableServices: BookableServiceV2[] | null;
    onClose?: () => void;
    onBookingCreated?: () => void;
}) {
    const [isResetTimer, setIsResetTimer] = useState(false);

    const toast = useToast();
    const confirmTimeOutDisclosure = useDisclosure();

    const { activeStep, setActiveStep } = useSteps({
        index: BookingStep.ChooseTime,
        count: bookingSteps.length,
    });

    const bookingForm = useForm<SubmitBookingFormSchemaValues>({
        resolver: zodResolver(submitBookingFormSchema),
        defaultValues: {
            oasBooking: {
                propertyId,
                contacts: [],
                nominatedContact: {
                    name: '',
                    email: '',
                    mobile: '',
                    isPrimaryContact: true,
                    isEnabled: false,
                },
                oasResult: {
                    technicianId: '',
                },
            },
        },
        mode: 'onChange',
    });

    const { data: bookableServices, isLoading } = useBookableServices({
        propertyId,
        overrideBookableServices,
    });
    const isBookable = !isLoading
        && (bookableServices !== undefined && bookableServices.length > 0);

    const complianceBookingTypes = useMemo(() => {
        // eslint-disable-next-line eqeqeq
        if (bookableServices == undefined || bookableServices?.length === 0) return null;

        return bookableServices?.[0]?.bookingTypes?.join(',');
    }, [bookableServices]);

    useEffect(() => {
        // set default value for booking step
        if (isOpen !== undefined && !isOpen) {
            bookingForm.reset();
            setActiveStep(BookingStep.ChooseTime);
            confirmTimeOutDisclosure.onClose();
        }

        // set default value for bookingTypes
        if (isBookable) {
            bookingForm.setValue('oasBooking.bookingTypes', complianceBookingTypes as string);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookableServices, isOpen]);

    const confirmBookingMutation = useConfirmBookingMutation(propertyId, {
        onSuccess: () => {
            toast({
                title: 'Booking submitted',
                description: 'Your booking has been submitted successfully',
                status: 'success',
            });

            // callback
            onBookingCreated?.();

            // clean up
            bookingForm.reset();
            setActiveStep(BookingStep.ChooseTime);
            onClose?.();
        },
    });

    const handleBack = () => {
        confirmBookingMutation.reset();
        bookingForm.clearErrors();
        setActiveStep(BookingStep.ChooseTime);
    };

    const handleResetTime = () => {
        setIsResetTimer((isResetValue) => !isResetValue);
    };

    const handleChooseContactScreen = () => {
        setActiveStep(BookingStep.Contacts);
    };

    const handleSubmit = () => {
        setActiveStep(BookingStep.Confirmation);
    };

    const handleClose = () => {
        bookingForm.reset();
        onClose?.();
        confirmTimeOutDisclosure.onClose();
        confirmBookingMutation.reset();
        setActiveStep(BookingStep.ChooseTime);
    };

    const handleConfirm = (vals: SubmitBookingFormSchemaValues) => {
        const { nominatedContact, contacts } = vals.oasBooking;
        let newContacts: Contact[] = [...contacts];

        // handle nominated contact & contacts array
        const hasNominatedContactData = nominatedContact.email !== ''
            && nominatedContact.mobile !== '' && nominatedContact.name !== '';

        if (nominatedContact.isEnabled && hasNominatedContactData) {
            const changedContacts = contacts.map((contact) => ({
                ...contact,
                isPrimaryContact: false,
            }));

            newContacts = [
                ...changedContacts, {
                    name: nominatedContact.name,
                    email: nominatedContact.email,
                    mobile: nominatedContact.mobile,
                    isPrimaryContact: true,
                }];
        }

        // then submit
        confirmBookingMutation.mutate({
            propertyId: vals.oasBooking.propertyId,
            bookingTypes: vals.oasBooking.bookingTypes,
            contacts: newContacts,
            oasResult: vals.oasBooking.oasResult,
        });
    };

    const onTimeUp = () => {
        // open popup to say time is up, continue booking or cancel
        if (confirmTimeOutDisclosure.isOpen) {
            confirmTimeOutDisclosure.onClose();
            setActiveStep(BookingStep.ChooseTime);

            // reset oasResult
            const { oasResult } = bookingForm.getValues('oasBooking');
            bookingForm.setValue('oasBooking.oasResult', {
                ...oasResult,
                oasMetadataId: '',
                startTime: '',
                endTime: '',
                technicianId: '',
            });

            // reset & clear errors
            bookingForm.clearErrors();
            confirmBookingMutation.reset();

            // reset timer
            handleResetTime();
        } else {
            confirmTimeOutDisclosure.onOpen();
        }
    };

    return {
        bookingForm,
        bookableServices,
        complianceBookingTypes,
        isBookable,
        isResetTimer,
        setIsResetTimer,
        activeStep,
        setActiveStep,
        onTimeUp,
        handleClose,
        handleResetTime,
        confirmBookingMutation,
        confirmTimeOutDisclosure,
        handleBack,
        handleSubmit,
        handleConfirm,
        handleChooseContactScreen,
    };
}

export { useBookingForm };
